/** A restrained Czech beer mug used as the recipe-colour swatch. */

import { useId } from 'react';
import { cn } from '@/lib/utils';

export interface BeerGlassProps {
  /** SRM/EBC swatch, `#RRGGBB`. Unused when {@link empty}. */
  readonly color?: string;
  readonly className?: string;
  /**
   * Looping pint pour that walks the SRM chart as it fills. The boot splash
   * uses this; the recipe swatch does not.
   */
  readonly loading?: boolean;
  /**
   * Settled pint: already full, foam still breathing. The sidebar wordmark
   * uses this so the mark is alive without the splash's fill loop.
   */
  readonly logo?: boolean;
  /**
   * Glass only — no liquid, no foam. The not-found page uses this so a missing
   * recipe and a mistyped URL share the same mug the rest of the app paints.
   */
  readonly empty?: boolean;
}

const BODY =
  'M15 15.5 C12.2 25 11.8 38.5 14.5 52 C15.8 59 20 63.5 26 65 H48 C54 63.5 58.2 59 59.5 52 C62.2 38.5 61.8 25 59 15.5 Z';

const CAVITY =
  'M18 17.5 C15.8 27.5 15.8 39 18 51 C19.2 57.5 22.5 60.5 27 62 H47 C51.5 60.5 54.8 57.5 56 51 C58.2 39 58.2 27.5 56 17.5 Z';

const BUBBLES = [
  { cx: 25, r: 0.65, delay: '0s', duration: '3.4s' },
  { cx: 39, r: 0.5, delay: '1.1s', duration: '3.8s' },
  { cx: 49, r: 0.6, delay: '2.2s', duration: '3.6s' },
] as const;

const FOAM_BUBBLES = [
  { cx: 19, cy: 17.5, r: 2.2, delay: '0s' },
  { cx: 26, cy: 12, r: 2.5, delay: '0.25s' },
  { cx: 34, cy: 8.5, r: 2.2, delay: '0.5s' },
  { cx: 43, cy: 8, r: 2.6, delay: '0.15s' },
  { cx: 51, cy: 13, r: 2.4, delay: '0.65s' },
  { cx: 57, cy: 18, r: 2, delay: '0.35s' },
] as const;

/**
 * Irregular surface — peaks and troughs do not repeat. CSS morphs `d` across four
 * of these; the first is the default so a still frame is still a pint.
 *
 * **The surface may not pass under the foam.** The crown's underside bottoms out
 * at y=26.175 (the `L56 25.5 C45 26.4 29 26.4 18 25.5` that closes its path), and
 * below that the beer used to show a flat strip of the seal band instead of foam.
 * Two things eat into the margin and both have to be paid for: the drift carries
 * the wave down by as much as 0.7, and the crest is a 1.15-wide stroke centred on
 * the path, so it hangs 0.575 below it. That leaves **24.9 as the deepest a
 * trough may sit**; the deepest here is 24.46, which lands at 25.735 once both are
 * added.
 *
 * Anything that edits these numbers — here or in `beer-wave-fill-morph` and
 * `beer-wave-crest-morph`, which override `d` and are the shapes actually seen —
 * has to keep that sum under 26.175.
 *
 * **What that ceiling costs, stated plainly.** Peaks sit at 21.2 and troughs at
 * 24.75, so the surface has 3.55 to move in where it once had 8. The amplitude is
 * the only thing that could give: the peaks are where they have always been, and
 * raising them to buy back depth was tried and looked like the wave was cutting
 * through the middle of the head. A deeper wave needs a deeper crown, not a lower
 * trough — move the `C45 26.4 29 26.4` underside down first, then this may follow.
 */
const WAVE_TOP =
  'M-8 23.11C-4.67 23.11 -1.33 21.38 2 21.38C5.33 21.38 8.67 24.4 12 24.4C15.33 24.4 18.67 22.04 22 22.04C25.33 22.04 28.67 24.75 32 24.75C35.33 24.75 38.67 22.44 42 22.44C45.33 22.44 48.67 21.29 52 21.29C55.33 21.29 58.67 24.04 62 24.04C65.33 24.04 68.67 23.11 72 23.11';

/**
 * The surface, skirted past the foot of the glass rather than stopped in the
 * beer.
 *
 * The skirt used to end at y=36, halfway down the liquid, which put a horizontal
 * edge inside the beer where the wave's layers stopped and the body's carried on.
 * Matching the colours across it — which took three passes to get right — only
 * ever made that edge faint: it is antialiased on every layer, and the drift
 * slides it up and down by a fraction of a pixel, so it surfaced as a line that
 * flickered along the waterline.
 *
 * 72 is the bottom of the viewBox, and the cavity clip discards everything below
 * the glass, so there is no longer an edge to see. The fill is opaque, so the
 * body it covers composites to exactly what the wave does. The horizontal -8 and
 * 72 are the same trick for the same reason: the wave has to overhang the space
 * it drifts through.
 */
const WAVE_FILL = `${WAVE_TOP} L72 72 L-8 72 Z`;

export function BeerGlass({
  color,
  className,
  loading = false,
  logo = false,
  empty = false,
}: BeerGlassProps) {
  const uid = useId().replace(/:/g, '');
  const cavityId = `${uid}-cavity`;
  const beerShadeId = `${uid}-beer-shade`;
  const glassId = `${uid}-glass`;
  const waveGlassId = `${uid}-wave-glass`;
  const foamId = `${uid}-foam`;
  const bodyId = `${uid}-body`;
  const outsideId = `${uid}-outside`;
  const belowRimId = `${uid}-below-rim`;
  // With a head on the beer, the glass stops just below the rim so its top
  // corners cannot peek out beside the foam. The glass-only mug has no foam
  // to sit behind, so it keeps its whole body and its rim.
  const rimClip = empty ? undefined : `url(#${belowRimId})`;
  const fill = color ?? '#00000000';

  return (
    <svg
      data-slot="beer-glass"
      data-loading={loading ? '' : undefined}
      data-logo={logo ? '' : undefined}
      data-empty={empty ? '' : undefined}
      viewBox="0 0 88 72"
      className={cn('h-12 w-14 overflow-visible', className)}
      aria-hidden="true"
    >
      <defs>
        <clipPath id={cavityId}>
          <path d={CAVITY} />
        </clipPath>
        {/* `userSpaceOnUse`, spanning exactly the liquid rect's own 12 → 62, so
            the body is painted precisely as it was under the default object
            bounding box — and so the wave can share it. Under the default the
            gradient stretches to whatever shape uses it, and the wave path runs
            -8 → 72 to survive its drift, so the two would have been shaded on
            different scales: the seam this exists to remove. */}
        <linearGradient
          id={beerShadeId}
          gradientUnits="userSpaceOnUse"
          x1="12"
          y1="0"
          x2="62"
          y2="0"
        >
          <stop offset="0%" stopColor="#000" stopOpacity="0.18" />
          <stop offset="28%" stopColor="#fff" stopOpacity="0.08" />
          <stop offset="72%" stopColor="#fff" stopOpacity="0" />
          <stop offset="100%" stopColor="#000" stopOpacity="0.28" />
        </linearGradient>
        <linearGradient id={glassId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.34" />
          <stop offset="28%" stopColor="#fff" stopOpacity="0.04" />
          <stop offset="72%" stopColor="#fff" stopOpacity="0.1" />
          <stop offset="100%" stopColor="#000" stopOpacity="0.12" />
        </linearGradient>
        {/* The glass sheen again, in user space over `BODY`'s own bounding box —
            12.6737 to 61.3263 across, 15.5 to 65 down, which is the box the
            default object-bounding-box mapping gives `glassId` on that path.
            Same stops, therefore the same colour at the same coordinate. It
            exists because the sheen is painted over the liquid at `BODY` and the
            wave is painted after it (the wave has to cut into the foam, and the
            foam comes later), so without this the wave is the one part of the
            beer wearing no glass at all — which is the colour difference, not
            the fill, which the stylesheet has always kept in step. `glassId`
            itself cannot simply be repinned: the foot below uses it too, on a
            bounding box of its own. */}
        <linearGradient
          id={waveGlassId}
          gradientUnits="userSpaceOnUse"
          x1="12.6737"
          y1="15.5"
          x2="61.3263"
          y2="65"
        >
          <stop offset="0%" stopColor="#fff" stopOpacity="0.34" />
          <stop offset="28%" stopColor="#fff" stopOpacity="0.04" />
          <stop offset="72%" stopColor="#fff" stopOpacity="0.1" />
          <stop offset="100%" stopColor="#000" stopOpacity="0.12" />
        </linearGradient>
        <linearGradient id={foamId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" className="beer-foam-lit" stopColor="#f6ebc4" />
          <stop offset="100%" className="beer-foam-shade" stopColor="#e5d3ad" />
        </linearGradient>
        {/* The glass's outer body. The foot is drawn wider than the glass and
            clipped to this, so its sides follow the rounded bottom rather than
            cutting straight lines across the corners. */}
        <clipPath id={bodyId}>
          <path d={BODY} />
        </clipPath>
        {/* Everything but the glass. The handle's outline ran on under the
            see-through wall and showed through it; clipped to this, the handle
            stops where the wall begins. */}
        <clipPath id={outsideId}>
          <path clipRule="evenodd" d={`M-20 -20 H120 V100 H-20 Z ${BODY}`} />
        </clipPath>
        <clipPath id={belowRimId}>
          <rect x="-20" y="18.5" width="140" height="100" />
        </clipPath>
      </defs>

      {/* A rounded ear handle, cut off where the glass wall begins. */}
      <path
        fill={`url(#${glassId})`}
        className="stroke-foreground/25 dark:stroke-white/30"
        strokeWidth="1.35"
        fillRule="evenodd"
        clipPath={`url(#${outsideId})`}
        strokeLinejoin="round"
        d="M56.5 22.5 C73 18.5 84.5 25.5 84.5 37.5 C84.5 50.5 72.5 57.5 56.5 52.5 L57.5 46.5 C69 50.5 77.8 45.8 78 37.8 C78.2 29.8 70.2 25.8 57.4 28.5 Z"
      />
      <path
        d="M61 24.5 C72.5 22 81 27.5 81.2 36"
        fill="none"
        stroke="#fff"
        strokeOpacity="0.28"
        strokeWidth="1.25"
        strokeLinecap="round"
      />

      {!empty && (
        <g clipPath={`url(#${cavityId})`}>
          <g className="beer-pour">
            <rect
              data-slot="beer-liquid"
              x="12"
              y="25"
              width="50"
              height="40"
              fill={fill}
              className={loading ? undefined : 'transition-[fill] duration-500'}
            />
            <rect x="12" y="25" width="50" height="40" fill={`url(#${beerShadeId})`} />
          </g>
          {!logo &&
            BUBBLES.map((bubble, index) => (
              <circle
                key={`${bubble.cx}-${bubble.delay}`}
                className="beer-bubble"
                cx={bubble.cx}
                cy="56"
                r={bubble.r}
                fill="#fff8e8"
                style={
                  loading
                    ? { animationDelay: `${index * 0.22}s` }
                    : {
                        animationDelay: `calc(0.85s + ${bubble.delay})`,
                        animationDuration: bubble.duration,
                      }
                }
              />
            ))}
        </g>
      )}

      <path d={BODY} fill={`url(#${glassId})`} clipPath={rimClip} />

      {/* Thick pressed foot with restrained vertical facets. */}
      <path
        d="M8 54 C23 58.5 51 58.5 66 54 L66 70 L8 70 Z"
        clipPath={`url(#${bodyId})`}
        fill={`url(#${glassId})`}
        opacity="0.82"
      />
      {[25, 31, 37, 43, 49].map((x) => (
        <path key={x} d={`M${x} 58 L${x - 1} 64`} className="stroke-white/25" strokeWidth="1" />
      ))}

      <path
        d={BODY}
        fill="none"
        clipPath={rimClip}
        className="stroke-foreground/25 dark:stroke-white/30"
        strokeWidth="1.35"
        strokeLinejoin="round"
      />
      {empty && (
        <ellipse
          cx="37"
          cy="15.5"
          rx="22"
          ry="2.5"
          fill="none"
          className="stroke-foreground/25 dark:stroke-white/30"
          strokeWidth="1"
        />
      )}

      {/* Opaque foam in the cavity at the beer line, unmoving. The crown
          animates above this; without it a lift would show the page through
          the glass. Logo only — the recipe swatch has no wave and must not
          paint a cream band over the SRM. */}
      {logo && (
        <g clipPath={`url(#${cavityId})`} className="beer-foam-seal">
          <rect x="16" y="18" width="44" height="18" fill={`url(#${foamId})`} />
        </g>
      )}

      {/* Foam is intentionally outside the cavity clip so its crown clears the rim. */}
      {!empty && (
        <g className="beer-foam">
          <path
            d="M15 22
             C12.5 18.5 14.5 15 18.5 15
             C18.5 11 22 8.8 25.5 11
             C26.2 6.5 30.5 4.2 34.5 7
             C37.5 2.5 43.5 3 46 9
             C50.5 6.5 54.8 9.5 54 14
             C58.5 13 61.5 17.5 59 22
             L56 25.5 C45 26.4 29 26.4 18 25.5 Z"
            fill={`url(#${foamId})`}
            className="beer-foam-edge"
            stroke="#e0cc9a"
            strokeOpacity="0.85"
            strokeWidth="0.8"
            strokeLinejoin="round"
          />
          {FOAM_BUBBLES.map((bubble) => (
            <circle
              key={bubble.cx}
              className="foam-bubble beer-foam-edge"
              cx={bubble.cx}
              cy={bubble.cy}
              r={bubble.r}
              fill={`url(#${foamId})`}
              stroke="#e0cc9a"
              strokeOpacity="0.85"
              strokeWidth="0.8"
              style={{ animationDelay: bubble.delay }}
            />
          ))}
          <circle
            cx="29"
            cy="8.8"
            r="1.2"
            className="beer-foam-glint"
            fill="#fff8e8"
            opacity="0.9"
          />
          <circle
            cx="36"
            cy="5.8"
            r="1.3"
            className="beer-foam-glint"
            fill="#fff8e8"
            opacity="0.9"
          />
          <circle cx="45" cy="10" r="1" className="beer-foam-glint" fill="#fff8e8" opacity="0.85" />
          <path
            className="beer-meniscus"
            d="M18 25.5 C29 26.4 45 26.4 56 25.5"
            fill="none"
            stroke={fill}
            strokeOpacity="0.4"
          />
        </g>
      )}

      {/* Drawn after the foam so the surface cuts a wave into the head. Logo
          only: the recipe swatch should not slosh. */}
      {logo && (
        <g clipPath={`url(#${cavityId})`}>
          <g className="beer-wave">
            <path data-slot="beer-wave-fill" d={WAVE_FILL} fill={fill} />
            {/* The body is two passes — flat colour, then this shade over it —
                and the wave was only ever the first of them, which is why the
                surface read as a lighter orange than the beer directly beneath
                it. Same `d` as the fill so it morphs with it (see the shared
                animation in `styles.css`), and a slot of its own rather than
                `beer-wave-fill`, because that one is also what the logo's
                colour rule paints and it would overwrite this gradient. */}
            <path data-slot="beer-wave-shade" d={WAVE_FILL} fill={`url(#${beerShadeId})`} />
            {/* Three passes on the beer and three on the wave: flat colour, the
                shade, and the glass over both. The body has had all three since
                the beginning; the wave had the first, then the first two, and
                looked like a different beer each time. */}
            <path data-slot="beer-wave-glass" d={WAVE_FILL} fill={`url(#${waveGlassId})`} />
            <path
              className="beer-wave-crest"
              d={WAVE_TOP}
              fill="none"
              stroke="#fff8e8"
              strokeOpacity="0.55"
              strokeWidth="1.15"
              strokeLinejoin="round"
            />
          </g>
        </g>
      )}

      <path
        // Below the foam when there is one, so the streak shines on the glass
        // rather than running up through the head.
        d={empty ? 'M19 20 C16.5 31 17 45 20 54' : 'M17.6 29 C16.8 37 17.3 46 20 54'}
        fill="none"
        stroke="#fff"
        strokeOpacity="0.27"
        strokeWidth="1.4"
        strokeLinecap="round"
      />
    </svg>
  );
}
